import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import {
  Button,
  Menu,
  MenuButton,
  MenuContent,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
} from '@apollo/orbit';
import classnames from 'classnames';
import React from 'react';
import { useLocation, useRouteMatch } from 'react-router';
import { Link } from 'react-router-dom';

import { fieldRouteConfig, insightsRouteConfig } from 'src/app/graph/routes';
import { useRouteParams } from 'src/hooks/useRouteParams';
import {
  TrackingEvent,
  useTrackCustomEvent,
} from 'src/hooks/useTrackCustomEvent';
import { EventCategory } from 'src/lib/analytics';
import { useShowSubgraphInsightsUI } from 'src/lib/featureFlags';
import { CoordinateKind } from 'src/lib/graphqlTypes/types';

type TabName = NonNullable<
  Parameters<typeof fieldRouteConfig.location>[0]['insightsTab']
>;

const insightsTabText: { [key in TabName]: string } = {
  operations: 'Operations',
  fields: 'Fields',
  objectFields: 'Object Fields',
  inputFields: 'Input Fields',
  enumValues: 'Enum Values',
  subgraphs: 'Subgraphs',
};

export function InsightsL3Menu() {
  let currentFieldType: string | undefined;
  let currentFieldName: string | undefined;
  const showSubgraphInsights = useShowSubgraphInsightsUI();

  const location = useLocation();
  const isOnFLP = useRouteMatch(fieldRouteConfig.definition);
  if (isOnFLP) {
    const { typeName, fieldName } = fieldRouteConfig.parseParams(location);
    currentFieldType = typeName;
    currentFieldName = fieldName;
  }

  const {
    insightsTab,
    previousFieldName,
    previousFieldType,
    previousInputFieldNamedAttribute,
    previousInputFieldNamedType,
    previousEnumNamedAttribute,
    previousEnumNamedType,
  } = useRouteParams(insightsRouteConfig);

  let previousCoordinate = {};
  switch (insightsTab) {
    case 'fields':
    case 'objectFields':
      previousCoordinate = {
        previousFieldType: currentFieldType,
        previousFieldName: currentFieldName,
      };
      break;
    case 'inputFields':
      previousCoordinate = {
        previousInputFieldNamedType: currentFieldType,
        previousInputFieldNamedAttribute: currentFieldName,
      };
      break;
    case 'enumValues':
      previousCoordinate = {
        previousEnumNamedType: currentFieldType,
        previousEnumNamedAttribute: currentFieldName,
      };
      break;
    default:
      break;
  }

  const trackCustomEvent = useTrackCustomEvent();
  const handleSelect = (tabName: TabName) => () => {
    const eventByTab: Record<TabName, TrackingEvent<EventCategory>> = {
      operations: {
        category: 'Insights L3 Operations List',
        action: 'selected_operations_tab',
      },
      fields: {
        category: 'Insights L3 Fields List',
        action: 'selected_fields_tab',
      },
      objectFields: {
        category: 'Insights L3 Object Fields List',
        action: 'selected_object_fields_tab',
      },
      inputFields: {
        category: 'Insights L3 Input Fields List',
        action: 'selected_input_fields_tab',
      },
      enumValues: {
        category: 'Insights L3 Enum Values List',
        action: 'selected_enum_values_tab',
      },
      subgraphs: {
        category: 'Insights L3 Subgraphs List',
        action: 'selected_subgraphs_tab',
      },
    };

    trackCustomEvent(eventByTab[tabName]);
  };

  if (!insightsTab) {
    throw new Error('Expected an insights tab to be selected');
  }

  return (
    <Menu>
      <MenuButton
        className="w-52 justify-between"
        as={Button}
        variant="secondary"
        value={insightsTab}
        rightIcon={<IconChevronDown />}
      >
        {insightsTabText[insightsTab]}
      </MenuButton>
      <MenuContent>
        <MenuList>
          <MenuGroup>
            <MenuItem
              className={classnames({
                'bg-btn-secondary-selected': insightsTab === 'operations',
              })}
              onClick={handleSelect('operations')}
              as={Link}
              to={insightsRouteConfig.locationFrom({
                location,
                patch: {
                  insightsTab: 'operations',
                  ...previousCoordinate,
                },
                fromRouteConfig: isOnFLP ? fieldRouteConfig : undefined,
              })}
            >
              Operations
            </MenuItem>
          </MenuGroup>
          <MenuDivider />
          {showSubgraphInsights && (
            <>
              <MenuGroup>
                <MenuItem
                  className={classnames({
                    'bg-btn-secondary-selected': insightsTab === 'subgraphs',
                  })}
                  onClick={handleSelect('subgraphs')}
                  as={Link}
                  to={insightsRouteConfig.locationFrom({
                    location,
                    patch: {
                      insightsTab: 'subgraphs',
                      ...previousCoordinate,
                    },
                    fromRouteConfig: insightsRouteConfig,
                  })}
                >
                  Subgraphs
                </MenuItem>
              </MenuGroup>
              <MenuDivider />
            </>
          )}
          <MenuGroup>
            <MenuItem
              className={classnames({
                'bg-btn-secondary-selected': insightsTab === 'objectFields',
              })}
              onClick={handleSelect('objectFields')}
              as={Link}
              to={
                previousFieldType && previousFieldName
                  ? fieldRouteConfig.locationFrom({
                      location,
                      patch: {
                        insightsTab: 'objectFields',
                        typeName: previousFieldType,
                        fieldName: previousFieldName,
                        coordinateKind: CoordinateKind.OBJECT_FIELD,
                        ...previousCoordinate,
                      },
                      fromRouteConfig: insightsRouteConfig,
                    })
                  : insightsRouteConfig.locationFrom({
                      location,
                      patch: {
                        insightsTab: 'objectFields',
                        coordinateKind: CoordinateKind.OBJECT_FIELD,
                        ...previousCoordinate,
                      },
                    })
              }
            >
              Object Fields
            </MenuItem>
          </MenuGroup>
          <MenuGroup>
            <MenuItem
              className={classnames({
                'bg-btn-secondary-selected': insightsTab === 'inputFields',
              })}
              onClick={handleSelect('inputFields')}
              as={Link}
              to={
                previousInputFieldNamedAttribute && previousInputFieldNamedType
                  ? fieldRouteConfig.locationFrom({
                      location,
                      patch: {
                        insightsTab: 'inputFields',
                        typeName: previousInputFieldNamedType,
                        fieldName: previousInputFieldNamedAttribute,
                        coordinateKind: CoordinateKind.INPUT_OBJECT_FIELD,
                        ...previousCoordinate,
                      },
                      fromRouteConfig: insightsRouteConfig,
                    })
                  : insightsRouteConfig.locationFrom({
                      location,
                      patch: {
                        insightsTab: 'inputFields',
                        coordinateKind: CoordinateKind.INPUT_OBJECT_FIELD,
                        ...previousCoordinate,
                      },
                    })
              }
            >
              Input Fields
            </MenuItem>
          </MenuGroup>
          <MenuDivider />
          <MenuGroup>
            <MenuItem
              className={classnames({
                'bg-btn-secondary-selected': insightsTab === 'enumValues',
              })}
              onClick={handleSelect('enumValues')}
              as={Link}
              to={
                previousEnumNamedAttribute && previousEnumNamedType
                  ? fieldRouteConfig.locationFrom({
                      location,
                      patch: {
                        insightsTab: 'enumValues',
                        typeName: previousEnumNamedType,
                        fieldName: previousEnumNamedAttribute,
                        coordinateKind: CoordinateKind.ENUM_VALUE,
                        ...previousCoordinate,
                      },
                      fromRouteConfig: insightsRouteConfig,
                    })
                  : insightsRouteConfig.locationFrom({
                      location,
                      patch: {
                        insightsTab: 'enumValues',
                        coordinateKind: CoordinateKind.ENUM_VALUE,
                        ...previousCoordinate,
                      },
                    })
              }
            >
              Enum Values
            </MenuItem>
          </MenuGroup>
        </MenuList>
      </MenuContent>
    </Menu>
  );
}
